import React from "react";

const PROVIDERS = [
  {
    name: "Eclectics (KentaPay / Swivel)",
    type: "Payout — Kenya (primary partner)",
    methods: ["M-Pesa B2C", "PesaLink"],
    corridors: ["CA-KE", "US-KE"],
    status: "integrating",
    icon: "🇰🇪",
    description: "Primary Kenya payout partner (CBK-regulated aggregator). Integration built and tested in SIT; awaiting UAT credentials.",
    latency: "Instant (M-Pesa) · Near-instant (PesaLink)",
    limits: "Per Eclectics agreement (pending)",
  },
  {
    name: "Africa's Talking",
    type: "Airtime & SMS — Kenya",
    methods: ["Airtime top-up", "SMS / OTP"],
    corridors: ["KE"],
    status: "active",
    icon: "📨",
    description: "Airtime top-ups and transactional SMS (OTP codes, notifications).",
    latency: "Instant",
    limits: "Per account balance",
  },
  {
    name: "Zum Rails",
    type: "Funding — Canada & United States",
    methods: ["Interac e-Transfer", "EFT", "PAD", "FedNow", "RTP", "ACH"],
    corridors: ["CA-KE", "CA-GH", "CA-NG", "CA-ZA", "US-KE", "US-GH", "US-NG", "US-ZA"],
    status: "active",
    icon: "🏦",
    description: "Unified North American payment rail for both CAD and USD origination. Single sandbox integration covering all ZoomPay corridors.",
    snpn: "Canada: Interac e-Transfer (instant) · US: FedNow / RTP (instant)",
    snpl: "Canada: PAD / EFT (1–2 days) · US: ACH (1–3 business days)",
    latency: "Instant (FedNow/RTP/Interac) · 1–3 days (ACH/EFT/PAD)",
    limits: "Up to $3,000 CAD / $5,000 USD per transaction",
  },
  {
    name: "Equity Bank Kenya",
    type: "Payout — Kenya (standby)",
    methods: ["M-Pesa", "Bank Transfer"],
    corridors: ["CA-KE", "US-KE"],
    status: "standby",
    icon: "📱",
    description: "Standby. Retained for future payout orchestration alongside Eclectics; commercial terms not yet finalized.",
    latency: "Instant (M-Pesa) · Same day (Bank)",
    limits: "Up to KES 300,000 per transaction",
  },
  {
    name: "I&M Bank Kenya",
    type: "Payout — Kenya (standby)",
    methods: ["M-Pesa B2C", "PesaLink"],
    corridors: ["CA-KE", "US-KE"],
    status: "standby",
    icon: "🏦",
    description: "Standby. PGS onboarding submitted; retained for payout orchestration and failover alongside Eclectics.",
    latency: "Instant (M-Pesa) · Near-instant (PesaLink)",
    limits: "Per I&M agreement (pending)",
  },
  {
    name: "Flutterwave",
    type: "Payout — West Africa",
    methods: ["Mobile Money", "Bank Transfer"],
    corridors: ["CA-GH", "US-GH", "CA-NG", "US-NG"],
    status: "active",
    icon: "🌍",
    description: "Multi-currency payouts across Ghana (GHS) and Nigeria (NGN) via mobile money and bank rails.",
    latency: "Instant – 1 business day",
    limits: "Up to $2,000 USD equivalent",
  },
  {
    name: "Cellulant",
    type: "Payout — East Africa",
    methods: ["Mobile Money", "Bank Transfer"],
    corridors: ["CA-UG", "CA-TZ", "CA-RW"],
    status: "coming_soon",
    icon: "📡",
    description: "Pan-African mobile money network covering Uganda, Tanzania, and Rwanda.",
    latency: "Instant – 2 business days",
    limits: "Up to $1,500 USD equivalent",
  },
  {
    name: "Peach Payments",
    type: "Payout — South Africa",
    methods: ["Bank Transfer", "EFT"],
    corridors: ["CA-ZA", "US-ZA"],
    status: "coming_soon",
    icon: "🇿🇦",
    description: "ZAR disbursements via South African banking rails.",
    latency: "Same day – 1 business day",
    limits: "Up to ZAR 50,000 per transaction",
  },
];

const statusBadge = (s) => {
  if (s === "active") return <span className="px-2 py-1 rounded text-xs font-bold bg-green-100 text-green-800">● ACTIVE</span>;
  if (s === "integrating") return <span className="px-2 py-1 rounded text-xs font-bold bg-blue-100 text-blue-800">◐ INTEGRATING</span>;
  if (s === "standby") return <span className="px-2 py-1 rounded text-xs font-bold bg-gray-200 text-gray-700">◌ STANDBY</span>;
  return <span className="px-2 py-1 rounded text-xs font-bold bg-yellow-100 text-yellow-800">⏳ SOON</span>;
};

function ProviderCard({ p, dimmed }) {
  return (
    <div className={`border rounded-lg p-5 shadow-sm ${dimmed ? 'bg-gray-50 opacity-75' : 'bg-white'}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{p.icon}</span>
          <div>
            <p className={`font-bold ${dimmed ? 'text-gray-700' : 'text-gray-900'}`}>{p.name}</p>
            <p className="text-xs text-gray-500">{p.type}</p>
          </div>
        </div>
        {statusBadge(p.status)}
      </div>
      <p className={`text-sm mb-3 ${dimmed ? 'text-gray-500' : 'text-gray-600'}`}>{p.description}</p>
      <div className="space-y-1.5 text-xs">
        {p.snpn && (
          <div className="flex justify-between">
            <span className="text-gray-500">⚡ SNPN rail</span>
            <span className="font-medium text-blue-700">{p.snpn}</span>
          </div>
        )}
        {p.snpl && (
          <div className="flex justify-between">
            <span className="text-gray-500">📅 SNPL rail</span>
            <span className="font-medium text-yellow-700">{p.snpl}</span>
          </div>
        )}
        {!p.snpn && (
          <div className="flex justify-between">
            <span className="text-gray-500">Methods</span>
            <span className="font-medium">{p.methods.join(", ")}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-500">Corridors</span>
          <span className="font-medium">{p.corridors.join(", ")}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Settlement</span>
          <span className="font-medium">{p.latency}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Limit</span>
          <span className="font-medium">{p.limits}</span>
        </div>
      </div>
    </div>
  );
}

// Runtime truth for Kenya payouts: what the system is actually configured to use.
function LiveRemittanceStrip({ p }) {
  if (!p) return null;
  const mode = p.modes?.REMITTANCE?.mode || "UNKNOWN";
  const auth = p.remittance?.auth;
  const cls = mode === "LIVE" ? "bg-red-100 text-red-800" : mode === "SANDBOX" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700";
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm bg-white border rounded-lg px-4 py-3">
      <span className="font-medium">Kenya payouts right now:</span>
      <span>{p.remittance?.provider || "none registered"}</span>
      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{mode}</span>
      {auth && <span className="text-gray-600">{auth.hasToken ? `Token valid until ${new Date(auth.expiresAt).toLocaleString()}` : "No active provider token"}</span>}
    </div>
  );
}

// Eclectics status follows the running system, so it can't drift between environments.
function withRuntimeStatus(list, rt) {
  const mode = rt?.remittance?.provider === "ECLECTICS" ? rt.modes?.REMITTANCE?.mode : null;
  return list.map((x) => {
    if (!x.name.startsWith("Eclectics")) return x;
    if (mode === "LIVE") return { ...x, status: "active", description: "Primary Kenya payout partner. Live: payouts are being sent through Eclectics." };
    if (mode === "SANDBOX") return { ...x, type: x.type + " · UAT", description: "Primary Kenya payout partner. Connected to the Eclectics UAT (test) environment." };
    return x;
  });
}

export default function CockpitProviders() {
  const [runtime, setRuntime] = React.useState(null);
  React.useEffect(() => {
    const API = process.env.REACT_APP_API_BASE_URL ?? "";
    fetch(`${API}/api/v2/cockpit/disbursements/providers`, { headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } })
      .then((r) => r.json()).then((d) => { if (d.ok) setRuntime(d); }).catch(() => {});
  }, []);
  const providers = withRuntimeStatus(PROVIDERS, runtime);
  const active = providers.filter(p => p.status === "active");
  const coming = providers.filter(p => p.status === "coming_soon");
  const integrating = providers.filter(p => p.status === "integrating");
  const standby = providers.filter(p => p.status === "standby");

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">🔌 Payment Providers</h1>
        <p className="text-gray-500 text-sm">Funding rails and payout network partners · Sandbox integrated</p>
      </div>

      <LiveRemittanceStrip p={runtime} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Active Partners", value: active.length, color: "bg-green-50 border-green-200" },
          { label: "Funding Countries", value: "CA + US", color: "bg-blue-50 border-blue-200" },
          { label: "Active Corridors", value: "5", color: "bg-purple-50 border-purple-200" },
          { label: "Coming Soon", value: coming.length, color: "bg-yellow-50 border-yellow-200" },
        ].map(c => (
          <div key={c.label} className={`border rounded-lg p-4 ${c.color}`}>
            <div className="text-2xl font-bold">{c.value}</div>
            <div className="text-xs text-gray-600 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Zum Rails highlight banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xl">🏦</span>
          <p className="font-bold text-blue-900">Zum Rails — Unified North American Funding Partner</p>
          <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-600 text-white">SANDBOX LIVE</span>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="bg-white rounded p-3 border border-blue-100">
            <p className="font-semibold text-blue-800 mb-1">⚡ SNPN — Send Now Pay Now</p>
            <p className="text-gray-600 text-xs">🇨🇦 Canada: Interac e-Transfer (instant settlement)</p>
            <p className="text-gray-600 text-xs">🇺🇸 United States: FedNow / RTP (instant settlement)</p>
          </div>
          <div className="bg-white rounded p-3 border border-blue-100">
            <p className="font-semibold text-yellow-800 mb-1">📅 SNPL — Send Now Pay Later</p>
            <p className="text-gray-600 text-xs">🇨🇦 Canada: PAD / EFT (1–2 business days)</p>
            <p className="text-gray-600 text-xs">🇺🇸 United States: ACH (1–3 business days)</p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-bold text-lg mb-3">✅ Active Partners ({active.length})</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {active.map(p => <ProviderCard key={p.name} p={p} dimmed={false} />)}
        </div>
      </div>

      {integrating.length > 0 && (
        <div>
          <h2 className="font-bold text-lg mb-3">◐ Integrating ({integrating.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {integrating.map(p => <ProviderCard key={p.name} p={p} dimmed={false} />)}
          </div>
        </div>
      )}

      {standby.length > 0 && (
        <div>
          <h2 className="font-bold text-lg mb-3">◌ Standby ({standby.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {standby.map(p => <ProviderCard key={p.name} p={p} dimmed={true} />)}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-bold text-lg mb-3">⏳ Coming Soon ({coming.length})</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {coming.map(p => <ProviderCard key={p.name} p={p} dimmed={true} />)}
        </div>
      </div>
    </div>
  );
}
